import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PageHero } from '../components/PageHero';
import { CheckCircle, ArrowRight, TrendingUp, Search, Share2, Target, BarChart2 } from 'lucide-react';

const PAKKETTEN = [
  {
    name: 'Growth',
    price: '€750',
    period: '/maand',
    kleur: 'white',
    popular: false,
    desc: 'Voor kleine bedrijven die structureel willen groeien.',
    includes: ['SEO basis monitoring','Maandelijkse rapportage','Lokale zichtbaarheid','Google Bedrijfsprofiel beheer','1x content/social post p.w.'],
    duur: 'Min. 3 maanden',
  },
  {
    name: 'Scale',
    price: '€1.500',
    period: '/maand',
    kleur: 'dark',
    popular: true,
    desc: 'Multi-channel marketing voor groeiende organisaties.',
    includes: ['Alles van Growth, plus:','Google Ads beheer','Meta Ads setup & beheer','Uitgebreide SEO-uitvoering','3x content/social posts p.w.','Wekelijkse rapportage'],
    duur: 'Min. 6 maanden',
  },
  {
    name: 'Dominance',
    price: 'v.a. €3.000',
    period: '/maand',
    kleur: 'white',
    popular: false,
    desc: 'Volledige marketing ontzorging voor ambitieuze bedrijven.',
    includes: ['Alles van Scale, plus:','LinkedIn Ads','TikTok Ads','Landingspagina\'s bouwen','Leadmachine & funnels','Dedicated accountmanager','Dagelijkse rapportage'],
    duur: 'Min. 12 maanden',
  },
];

const MODULES = [
  { icon: Search, title: 'SEO', desc: 'Maandelijkse optimalisatie voor hogere Google-rankings.' },
  { icon: Target, title: 'Google Ads', desc: 'Campagnebeheer voor maximale ROI op advertentiebudget.' },
  { icon: Share2, title: 'Social Media', desc: 'Content creatie en community beheer op alle kanalen.' },
  { icon: TrendingUp, title: 'Leadgeneratie', desc: 'Funnels en automation voor een constante leadstroom.' },
  { icon: BarChart2, title: 'Rapportage', desc: 'Maandelijkse inzichten en bijsturing op basis van data.' },
];

const PakkettenVisual = () => (
  <div className="space-y-3 w-full max-w-xs">
    {PAKKETTEN.map(p => (
      <div key={p.name} className={`rounded-xl p-4 flex items-center justify-between ${p.popular ? 'bg-[#D4A017]' : 'bg-white/10'}`}>
        <div>
          <p className={`font-bold text-sm ${p.popular ? 'text-[#0f172a]' : 'text-white'}`}>{p.name}</p>
          <p className={`text-xs ${p.popular ? 'text-[#0f172a]/70' : 'text-[#94a3b8]'}`}>{p.duur}</p>
        </div>
        <span className={`font-bold text-sm ${p.popular ? 'text-[#0f172a]' : 'text-[#D4A017]'}`}>{p.price}</span>
      </div>
    ))}
  </div>
);

export default function PakkettenPage() {
  return (
    <>
      <SEOHead
        title="Maandelijkse Marketing Pakketten — Growth, Scale & Dominance | IMPERIA AI"
        description="Kies uw maandelijks marketingpakket. Van SEO en lokale zichtbaarheid tot volledige multi-channel marketing ontzorging. Growth €750 — Scale €1.500 — Dominance v.a. €3.000/mnd."
        keywords="marketing pakketten maandelijks, seo abonnement, marketing bureau pakket, google ads beheer, groei pakket"
        canonical="/pakketten"
      />

      <PageHero
        badge="Maandelijkse pakketten"
        title={<>Doorlopende marketing die <span className="text-[#D4A017]">elke maand</span> groei levert</>}
        subtitle="Voor bedrijven die structureel online willen groeien. Wij voeren maandelijks alles uit — u focust op uw bedrijf."
        ctaPrimary={{ label: 'Offerte aanvragen', to: '/offerte' }}
        ctaSecondary={{ label: 'Bekijk pakketten', to: '#pakketten' }}
        visual={<PakkettenVisual />}
      />

      {/* Modules */}
      <section className="bg-[#f8fafc] py-14">
        <div className="container-custom max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl font-bold text-[#0f172a] mb-3">Wat zit er in onze pakketten?</h2>
            <p className="text-[#64748b]">Afhankelijk van uw pakket voeren we deze marketing-disciplines uit.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {MODULES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-5 shadow-sm text-center">
                <div className="w-10 h-10 bg-[#D4A017]/15 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-5 h-5 text-[#D4A017]" />
                </div>
                <h3 className="font-bold text-[#0f172a] text-sm mb-1">{title}</h3>
                <p className="text-[#64748b] text-xs">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pakketten vergelijking */}
      <section id="pakketten" className="bg-white py-16">
        <div className="container-custom max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">Kies uw groeipakket</h2>
            <p className="text-[#64748b]">Minimale contractduur voor meetbare resultaten. Geen verspilde maanden.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PAKKETTEN.map(p => (
              <div key={p.name} className={`rounded-2xl p-8 relative ${p.popular ? 'bg-[#0f172a] border-2 border-[#D4A017]' : 'bg-[#f8fafc] border border-gray-200'}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A017] text-[#0f172a] text-xs font-bold px-4 py-1 rounded-full">MEEST GEKOZEN</div>}
                <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${p.popular ? 'text-[#D4A017]' : 'text-[#64748b]'}`}>{p.name}</p>
                <p className={`text-3xl font-bold ${p.popular ? 'text-white' : 'text-[#0f172a]'}`}>{p.price}<span className="text-base font-normal opacity-60">{p.period}</span></p>
                <p className={`text-sm mt-2 mb-2 ${p.popular ? 'text-[#94a3b8]' : 'text-[#64748b]'}`}>{p.desc}</p>
                <p className={`text-xs font-semibold mb-5 ${p.popular ? 'text-[#D4A017]' : 'text-[#D4A017]'}`}>{p.duur}</p>
                <ul className="space-y-2 mb-6">
                  {p.includes.map((f, i) => (
                    <li key={f} className={`text-sm flex items-start gap-2 ${i === 0 && p.name !== 'Growth' ? (p.popular ? 'text-[#D4A017] font-semibold' : 'text-[#D4A017] font-semibold') : p.popular ? 'text-[#94a3b8]' : 'text-[#64748b]'}`}>
                      {(i > 0 || p.name === 'Growth') && <CheckCircle className="w-3.5 h-3.5 text-[#D4A017] mt-0.5 flex-shrink-0" />}
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/offerte?type=abonnement" className={`block text-center font-bold py-3 rounded-xl transition-all ${p.popular ? 'bg-[#D4A017] text-[#0f172a] hover:bg-[#B8860B]' : 'bg-[#0f172a] text-white hover:bg-[#1e293b]'}`}>
                  Offerte aanvragen
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D4A017] py-14">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">Weet u niet welk pakket past?</h2>
          <p className="text-[#1e293b] mb-8">Start met een gratis scan — daarna adviseren wij u welk pakket het beste aansluit bij uw situatie.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-bold px-8 py-4 rounded-xl transition-all">
              Gratis scan starten <ArrowRight className="inline ml-2" size={16} />
            </Link>
            <Link to="/offerte" className="bg-[#0f172a]/20 hover:bg-[#0f172a]/30 text-[#0f172a] font-bold px-8 py-4 rounded-xl transition-all">
              Direct offerte aanvragen
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
